import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Loader2, Send, RefreshCw, Zap, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  bounced: 'bg-red-50 text-red-800 border-red-300',
  opened: 'bg-sky-50 text-sky-700 border-sky-200',
  clicked: 'bg-violet-50 text-violet-700 border-violet-200',
  dry_run: 'bg-stone-50 text-stone-700 border-stone-200',
};

export default function EmailDiagnosticsPage() {
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ to_email: '', template_key: '', status: '' });
  const [detail, setDetail] = useState(null);
  const [stressOpen, setStressOpen] = useState(false);
  const [stressMode, setStressMode] = useState('dry_run');
  const [stressRunning, setStressRunning] = useState(false);
  const [stressResult, setStressResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams({ page: String(page), limit: '50' });
      if (filters.to_email) qp.set('to_email', filters.to_email);
      if (filters.template_key) qp.set('template_key', filters.template_key);
      if (filters.status) qp.set('status', filters.status);
      const [s, d] = await Promise.all([
        api.get('/admin/email-diagnostics/stats'),
        api.get(`/admin/email-diagnostics/deliveries?${qp}`),
      ]);
      setStats(s.data);
      setDeliveries(d.data.deliveries || []);
      setTotal(d.data.total || 0);
    } catch { toast.error('Failed to load diagnostics'); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/admin/email-diagnostics/deliveries/${id}`);
      setDetail(r.data);
    } catch { toast.error('Failed to load delivery'); }
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/email-diagnostics/deliveries/${id}/resend`);
      toast.success('Resent');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
  };

  const runStress = async () => {
    setStressRunning(true);
    setStressResult(null);
    try {
      const r = await api.post('/admin/email-diagnostics/stress-test', {
        mode: stressMode,
        concurrency: 2,
      });
      setStressResult(r.data);
      toast.success(`${stressMode === 'live' ? 'Stress test' : 'Dry run'} complete`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Stress test failed'); }
    finally { setStressRunning(false); }
  };

  return (
    <div data-testid="email-diagnostics-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Email Diagnostics</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="h-3 w-3" /> Refresh
          </Button>
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setStressOpen(true)} data-testid="open-stress-test">
            <Zap className="h-3 w-3" /> Stress Test
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
        {stats && [
          { label: 'Total', value: stats.total_deliveries },
          { label: 'Sent', value: stats.sent },
          { label: 'Delivered', value: stats.delivered, color: '#0E6B5D' },
          { label: 'Opened', value: stats.opened, color: '#1F5E7A' },
          { label: 'Clicked', value: stats.clicked, color: '#5B21B6' },
          { label: 'Failed', value: stats.failed, color: '#B91C1C' },
          { label: 'Bounced', value: stats.bounced, color: '#7F1D1D' },
        ].map((s, i) => (
          <Card key={i} className="bg-white border-stone/50">
            <CardContent className="p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className="text-base font-semibold" style={{ color: s.color || 'hsl(var(--ink))' }}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={filters.to_email}
            onChange={e => setFilters({ ...filters, to_email: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
            placeholder="Recipient email..."
            className="pl-8 h-8 text-xs"
            data-testid="filter-email"
          />
        </div>
        <Input
          value={filters.template_key}
          onChange={e => setFilters({ ...filters, template_key: e.target.value })}
          onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
          placeholder="Template key"
          className="h-8 text-xs w-40"
          data-testid="filter-template"
        />
        <Select value={filters.status || 'all'} onValueChange={v => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="opened">Opened</SelectItem>
            <SelectItem value="clicked">Clicked</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
            <SelectItem value="dry_run">Dry run</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setPage(1); load(); }}>Apply</Button>
      </div>

      <Separator className="mb-3" />

      {/* Deliveries list */}
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : deliveries.length === 0 ? (
        <Card className="bg-white border-stone/50"><CardContent className="p-6 text-center"><p className="text-sm text-muted-foreground">No deliveries match this filter.</p></CardContent></Card>
      ) : (
        <div className="space-y-1" data-testid="deliveries-list">
          {deliveries.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-stone/50 bg-white hover:bg-white/80" data-testid={`delivery-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium text-ink truncate">{d.subject || d.template_key}</span>
                  <Badge variant="outline" className="text-[9px]">{d.template_key}</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">to {d.to_email}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${STATUS_COLORS[d.status] || ''}`}>
                {d.status}
              </Badge>
              <span className="text-[10px] text-muted-foreground flex-shrink-0 hidden md:block">
                {d.created_at ? new Date(d.created_at).toLocaleString() : ''}
              </span>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openDetail(d.id)} data-testid={`view-delivery-${d.id}`}>
                <Eye className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => resend(d.id)} title="Resend" data-testid={`resend-delivery-${d.id}`}>
                <Send className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" className="text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" className="text-xs" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-sm">Delivery Detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground">Template</Label><p className="font-medium">{detail.template_key}</p></div>
                <div><Label className="text-[10px] text-muted-foreground">Status</Label><Badge variant="outline" className={STATUS_COLORS[detail.status] || ''}>{detail.status}</Badge></div>
                <div><Label className="text-[10px] text-muted-foreground">To</Label><p className="font-mono">{detail.to_email}</p></div>
                <div><Label className="text-[10px] text-muted-foreground">From</Label><p className="font-mono">{detail.from_email}</p></div>
                <div className="col-span-2"><Label className="text-[10px] text-muted-foreground">Subject</Label><p>{detail.subject}</p></div>
                <div><Label className="text-[10px] text-muted-foreground">Provider Msg ID</Label><p className="font-mono break-all text-[10px]">{detail.provider_message_id || '-'}</p></div>
                <div><Label className="text-[10px] text-muted-foreground">Attempts</Label><p>{detail.attempt_count}</p></div>
                {detail.error_message && (
                  <div className="col-span-2"><Label className="text-[10px] text-muted-foreground">Error</Label><p className="text-red-600">{detail.error_message}</p></div>
                )}
              </div>
              {(detail.provider_events || []).length > 0 && (
                <div>
                  <Label className="text-[10px] text-muted-foreground">Provider Events</Label>
                  <div className="mt-1 space-y-1">
                    {detail.provider_events.map((e, i) => (
                      <div key={i} className="flex items-center justify-between border-l-2 border-stone/40 pl-2 text-[11px]">
                        <span className="font-medium">{e.event_type}</span>
                        <span className="text-muted-foreground">{e.timestamp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stress test dialog */}
      <Dialog open={stressOpen} onOpenChange={setStressOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-sm">Email Stress Test</DialogTitle></DialogHeader>
          <div className="space-y-4 text-xs">
            <p className="text-muted-foreground">
              Sends one instance of each active template to the ADMIN_SEED_EMAILS. Dry-run mode logs a delivery record without hitting the provider.
            </p>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={stressMode} onValueChange={setStressMode}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="stress-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dry_run">Dry run (no send)</SelectItem>
                  <SelectItem value="live">Live send</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={runStress} disabled={stressRunning} data-testid="run-stress-btn">
                {stressRunning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1" />}
                Run {stressMode === 'live' ? 'Live' : 'Dry Run'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setStressOpen(false)}>Close</Button>
            </div>
            {stressResult && (
              <div className="border border-stone/50 rounded p-3 bg-stone-50/40">
                <p className="font-medium text-ink mb-1">Result</p>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div>Templates: <strong>{stressResult.templates_count}</strong></div>
                  <div>Total sends: <strong>{stressResult.total_sends}</strong></div>
                  <div className="text-emerald-700">Sent: <strong>{stressResult.sent}</strong></div>
                  <div className="text-red-600">Failed: <strong>{stressResult.failed}</strong></div>
                  <div className="text-red-800">Errors: <strong>{stressResult.errors}</strong></div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
